import { ColumnState } from "ag-grid-community";
import { DateRange } from "react-day-picker";
import { create } from "zustand";
import { getUserSearchFilterDefaults } from "src/hooks/useInitUserSearchFilter";
import {
	defaultKSK2040_R01HeaderDto,
	defaultKSK2040_R01DetailDto,
	KSK2040_R01DetailDto,
	KSK2040_R01HeaderDto,
} from "./Dto_KSK2040_R01";

export interface KSK2040_R01Filter {
	compCd: string;
	dateRange: DateRange | undefined;
	workplaceCd: string;
	itemCd: string;
	itemNm: string;
	productEmp: string;
	productEmpNm: string;
}

export interface Store_KSK2040_R01 {
	KSK2040_R01Filter: KSK2040_R01Filter;
	KSK2040_R01PastFilter: KSK2040_R01Filter;
	setKSK2040_R01Filter: (filter: Partial<KSK2040_R01Filter>) => void;
	setKSK2040_R01PastFilter: (filter: Partial<KSK2040_R01Filter>) => void;
	KSK2040_R01List: KSK2040_R01HeaderDto[];
	setKSK2040_R01List: (list: KSK2040_R01HeaderDto[]) => void;
	selectedKSK2040_R01: KSK2040_R01HeaderDto | undefined;
	setSelectedKSK2040_R01: (data: KSK2040_R01HeaderDto | undefined) => void;
	initSelectedKSK2040_R01: () => void;
	editKSK2040_R01: KSK2040_R01HeaderDto;
	setEditKSK2040_R01: (data: KSK2040_R01HeaderDto) => void;
	KSK2040_R01DetailList: KSK2040_R01DetailDto[];
	setKSK2040_R01DetailList: (list: KSK2040_R01DetailDto[]) => void;
	updateKSK2040_R01DetailList: (valueIndex: number, data: KSK2040_R01DetailDto) => void;
	originalKSK2040_R01DetailList: KSK2040_R01DetailDto[];
	setOriginalKSK2040_R01DetailList: (list: KSK2040_R01DetailDto[]) => void;
	columnState: ColumnState[] | null;
	setColumnState: (state: ColumnState[] | null) => void;
	detailColumnState: ColumnState[] | null;
	setDetailColumnState: (state: ColumnState[] | null) => void;
	scrollTop: number | null;
	setScrollTop: (top: number | null) => void;
	detailScrollTop: number | null;
	setDetailScrollTop: (top: number | null) => void;
	allCount: number;
	setAllCount: (allCount: number) => void;
	initData: () => void;
}

const createDefaultKSK2040_R01Filter = (): KSK2040_R01Filter => ({
	...getUserSearchFilterDefaults(),
	dateRange: {
		from: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
		to: new Date(),
	},
	itemCd: "",
	itemNm: "",
	productEmp: "",
	productEmpNm: "",
});

export const defaultKSK2040_R01Filter = createDefaultKSK2040_R01Filter();

const indexHeaders = (list: KSK2040_R01HeaderDto[]) => list.map((item, index) => ({
	...defaultKSK2040_R01HeaderDto,
	...item,
	valueIndex: index + 1,
	orderIndex: index + 1,
}));

const indexDetails = (list: KSK2040_R01DetailDto[]) => list.map((item, index) => ({
	...defaultKSK2040_R01DetailDto,
	...item,
	valueIndex: index + 1,
	orderIndex: index + 1,
}));

export const useKSK2040_R01Store = create<Store_KSK2040_R01>((set) => ({
	KSK2040_R01Filter: createDefaultKSK2040_R01Filter(),
	KSK2040_R01PastFilter: createDefaultKSK2040_R01Filter(),
	setKSK2040_R01Filter: (filter) => set((state) => ({
		KSK2040_R01Filter: { ...state.KSK2040_R01Filter, ...filter },
	})),
	setKSK2040_R01PastFilter: (filter) => set((state) => ({
		KSK2040_R01PastFilter: { ...state.KSK2040_R01PastFilter, ...filter },
	})),
	KSK2040_R01List: [],
	setKSK2040_R01List: (list) => set({ KSK2040_R01List: indexHeaders(list) }),
	selectedKSK2040_R01: undefined,
	setSelectedKSK2040_R01: (data) => set({ selectedKSK2040_R01: data }),
	initSelectedKSK2040_R01: () => set({ selectedKSK2040_R01: undefined }),
	editKSK2040_R01: { ...defaultKSK2040_R01HeaderDto },
	setEditKSK2040_R01: (data) => set({ editKSK2040_R01: data }),
	KSK2040_R01DetailList: [],
	setKSK2040_R01DetailList: (list) => set({ KSK2040_R01DetailList: indexDetails(list) }),
	updateKSK2040_R01DetailList: (valueIndex, data) => set((state) => ({
		KSK2040_R01DetailList: state.KSK2040_R01DetailList.map((item) =>
			item.valueIndex === valueIndex ? data : item),
	})),
	originalKSK2040_R01DetailList: [],
	setOriginalKSK2040_R01DetailList: (list) => set({
		originalKSK2040_R01DetailList: indexDetails(list).map((item) => ({ ...item })),
	}),
	columnState: null,
	setColumnState: (columnState) => set({ columnState }),
	detailColumnState: null,
	setDetailColumnState: (detailColumnState) => set({ detailColumnState }),
	scrollTop: null,
	setScrollTop: (scrollTop) => set({ scrollTop }),
	detailScrollTop: null,
	setDetailScrollTop: (detailScrollTop) => set({ detailScrollTop }),
	allCount: 0,
	setAllCount: (allCount) => set({ allCount }),
	initData: () => set({
		KSK2040_R01Filter: createDefaultKSK2040_R01Filter(),
		KSK2040_R01PastFilter: createDefaultKSK2040_R01Filter(),
		KSK2040_R01List: [],
		selectedKSK2040_R01: undefined,
		editKSK2040_R01: { ...defaultKSK2040_R01HeaderDto },
		KSK2040_R01DetailList: [],
		originalKSK2040_R01DetailList: [],
		columnState: null,
		detailColumnState: null,
		scrollTop: null,
		detailScrollTop: null,
		allCount: 0,
	}),
}));
